import React, { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import * as styles from './StudyListItem.module.scss';
import { Card, Checkbox } from 'components/core';
import { Inline } from 'components/layout';

type StudyListItemProps = {
  title: string | null;
  uniqueIndex: number;
  isSelected: boolean;
  isActive?: boolean;
  handleSelectionChange: (isChecked: boolean) => void;
  justUndone: boolean;
  /**
   * Optional banner rendered full-width at the top of the card, above the
   * checkbox/content row. Used for the LibKey alert banner on single-reference
   * studies.
   */
  topBanner?: ReactNode;
};

export const StudyListItem = ({
  title,
  uniqueIndex,
  children,
  isSelected,
  isActive = false,
  handleSelectionChange,
  justUndone,
  topBanner,
}: PropsWithChildren<StudyListItemProps>) => {
  const ariaLabel = `Study #${uniqueIndex} ${title}`;

  const isFocused = justUndone || isActive;
  const focusRef = useRef<HTMLDivElement>(null);

  const revealFocusedStudy = () => {
    const element = focusRef.current;
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    if (!isFocused) return;

    revealFocusedStudy();
  }, [isFocused]);

  return (
    <div ref={focusRef}>
      <Card
        {...(isActive && { 'data-study-active': true })}
        data-testid={ariaLabel}
        data-testclass="study"
        className={classNames(styles.card, {
          [styles.isSelected]: isSelected,
          [styles.undone]: justUndone,
          [styles.isActive]: isActive,
        })}
      >
        {topBanner && <div className={styles.topBanner}>{topBanner}</div>}
        <Inline
          alignX="left"
          alignY="baseline"
          spacing={6}
          className={styles.cardContent}
        >
          <Checkbox
            size="md"
            onChange={(event) => {
              handleSelectionChange(event.currentTarget.checked);
            }}
            aria-label={ariaLabel}
            checked={isSelected}
          />
          <div className={styles.content}>{children}</div>
        </Inline>
      </Card>
    </div>
  );
};
